import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { MiniMonth } from './MiniMonth';
import { MyCalendars } from './MyCalendars';

/**
 * The calendar's left rail (#677): the mini-month, "My calendars" (#681 — a
 * calendar is a tag; `myCalendars` is the page's filter state and handlers, or
 * absent to leave the section out) and the connected-calendar status. The rail
 * itself fetches nothing; My calendars lazily fetches the tag vocabulary.
 */
export function CalendarRail({ anchorDay, todayKey, view, onPickDay, googleConnected, outlookStatus, statusLoading, myCalendars }) {
    return (
        <aside
            className="flex flex-col w-[238px] flex-none box-border bg-[#FBFAF9] border-r border-[#E9E7E2] px-3.5 pt-3.5 pb-6 overflow-y-auto"
            aria-label="Calendar navigation"
            data-testid="calendar-rail"
        >
            <MiniMonth anchorDay={anchorDay} todayKey={todayKey} view={view} onPickDay={onPickDay} />
            {myCalendars && (
                <>
                    <div className="h-px bg-[#EDEBE6] my-4" />
                    <MyCalendars {...myCalendars} />
                </>
            )}
            <div className="flex-1" />
            <div className="h-px bg-[#EDEBE6] my-4" />
            <ConnectedCalendars googleConnected={googleConnected} outlookStatus={outlookStatus} loading={statusLoading} />
        </aside>
    );
}

/** A rail section heading with an optional trailing action (prototype). */
export function RailSection({ title, action, children }) {
    return (
        <section className="mt-4 first:mt-0">
            <div className="flex items-center gap-2 mb-2">
                <h2 className="flex-1 text-[10.5px] font-semibold tracking-[0.09em] uppercase text-[#A29B95]">{title}</h2>
                {action}
            </div>
            {children}
        </section>
    );
}

/**
 * A checkbox row for a rail filter (prototype `myCalendars` row). `count`
 * renders only when it is a number (mail's rule: no badge beats a wrong one).
 */
export function RailCheckboxRow({ label, checked, onChange, color, testId, count, disabled = false, title }) {
    return (
        <button
            type="button"
            role="checkbox"
            aria-checked={checked}
            disabled={disabled}
            title={title}
            onClick={() => onChange(!checked)}
            className="w-full flex items-center gap-[9px] h-8 px-2 rounded-lg text-left hover:bg-[#F2F0EC] disabled:opacity-50 disabled:hover:bg-transparent disabled:cursor-not-allowed"
            data-testid={testId}
        >
            <span
                className="flex-none w-[15px] h-[15px] rounded flex items-center justify-center border-[1.5px]"
                style={checked
                    ? { borderColor: color || '#4D11F6', background: color || '#4D11F6' }
                    : { borderColor: '#C9C4BD' }}
                aria-hidden="true"
            >
                {checked && <Check className="w-[11px] h-[11px] text-white" strokeWidth={3} />}
            </span>
            <span className="flex-1 min-w-0 text-[12.5px] text-[#3A3532] truncate">{label}</span>
            {typeof count === 'number' && (
                <span className="flex-none text-[11px] tabular-nums text-[#A29B95]" data-testid={testId ? `${testId}-count` : undefined}>{count}</span>
            )}
        </button>
    );
}

/**
 * Provider connection state, kept out of the header (D7) but still legible.
 * The three Outlook states and their testids are the ones the old header
 * badges had. Reconnect navigates in-app instead of a full page reload.
 */
function ConnectedCalendars({ googleConnected, outlookStatus, loading }) {
    const dot = (cls) => <span className={`w-1.5 h-1.5 rounded-full flex-none ${cls}`} aria-hidden="true" />;
    const row = 'flex items-center gap-2 text-[12px] text-[#57504B] py-1';
    let outlook;
    if (!outlookStatus) {
        outlook = <div className={row} aria-label="Checking Outlook connection status">{dot('bg-[#D9D4CD]')}Outlook…</div>;
    } else if (outlookStatus.connected) {
        outlook = <div className={row} role="status" aria-label="Outlook calendar connected" data-testid="outlook-badge-connected">{dot('bg-[#34D188]')}Outlook</div>;
    } else if (outlookStatus.needs_reconnect) {
        outlook = (
            <Link to="/settings/integrations" className={`${row} text-[#92400E] hover:underline`} aria-label="Outlook calendar needs reconnection — go to settings" data-testid="outlook-badge-reconnect">
                {dot('bg-[#F59E0B]')}Outlook — reconnect
            </Link>
        );
    } else {
        outlook = <div className={`${row} text-[#A29B95]`} role="status" aria-label="Outlook calendar not connected" data-testid="outlook-badge-disconnected">{dot('bg-[#C9C4BD]')}Outlook not connected</div>;
    }
    return (
        <RailSection title="Connected calendars">
            <div className={`${row} ${googleConnected ? '' : 'text-[#A29B95]'}`} role="status" data-testid={googleConnected ? 'google-badge-connected' : 'google-badge-disconnected'}>
                {dot(loading ? 'bg-[#D9D4CD]' : googleConnected ? 'bg-[#34D188]' : 'bg-[#C9C4BD]')}
                {googleConnected ? 'Google Calendar' : 'Google Calendar not connected'}
            </div>
            {outlook}
            <Link to="/settings/integrations" className="block mt-1.5 text-[12px] font-medium text-[#4D11F6] hover:underline">Manage integrations</Link>
        </RailSection>
    );
}
